import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { SideBar } from "./sidebar";
import styles from "./styles.module.scss";
import { CreditBanner, CreditBannerVariant } from "components";
import { useDispatch } from "react-redux";
import { updatePreference } from "state";
import { PageHeader } from "../../components/header";
import { useLocation } from "react-router-dom";
import { useCredits } from "hooks/useCredits";
import { BuyAdditionalCreditModal } from "pages/pricing-v2/components/buy-additional-credit-modal";

export type tabs =
  | "dashboard"
  | "dssessment"
  | "settings"
  | "billings"
  | "report"
  | "assessment"
  | "jobs"
  | "library"
  | "bank"
  | "interview"
  | "billings"
  | "settings"
  | "support"
  | "knowledge"
  | "pricing";

interface LayoutProps {
  children: any;
  active: tabs;
  noPadding?: boolean;
}

const EmployerDashboardLayout: React.FC<LayoutProps> = ({
  children,
  active,
  noPadding
}) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { credits } = useCredits();
  const [showTopupModal, setShowTopupModal] = useState(false);

  // Persistent credit banner (pinned below the header). Exhausted takes
  // precedence over running-low. Only once credit data has loaded, so an
  // unloaded `0` balance never flashes an "exhausted" banner. Excluded from the
  // Hiring Assistant chat workspace (`active === 'assessment'`), whose
  // multi-pane full-height layout can't host a pinned banner — it already gates
  // credit-spending actions inline.
  const creditBannerVariant: CreditBannerVariant | null = !credits || active === "assessment"
    ? null
    : credits.isExhausted
      ? "exhausted"
      : credits.runningLow
        ? "low"
        : null;

  // Measure the banner so each page's scroll area can offset by its height via
  // the shared `--credit-banner-h` CSS variable.
  const bannerRef = useRef<HTMLDivElement>(null);
  const [bannerHeight, setBannerHeight] = useState(0);

  useLayoutEffect(() => {
    if (!creditBannerVariant) {
      setBannerHeight(0);
      return;
    }
    const measure = () => setBannerHeight(bannerRef.current?.offsetHeight ?? 0);
    measure();

    let observer: ResizeObserver | undefined;
    if (bannerRef.current && typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(measure);
      observer.observe(bannerRef.current);
    }
    window.addEventListener("resize", measure);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [creditBannerVariant]);

  useEffect(() => {
    const currency = localStorage.getItem("preferred_currency") ?? "USD";
    dispatch(updatePreference({ currency }));
  }, []);

  // Determine page title based on current route
  const getPageTitle = (): string => {
    const path = location.pathname;

    if (path.includes('/manage-jobs/review-talent') && path.includes('/report/')) {
      return "Review Talents";
    }
    if (path.includes('/manage-jobs/review-talent')) {
      return "Review Talents";
    }
    if (path.includes('/manage-jobs')) {
      return "Manage Jobs";
    }
    if (path.includes('/assessment') && !path.includes('/assessments')) {
      return "Hiring Assistant";
    }
    if (path.includes('/assessments/new')) {
      return "Create Assessment";
    }
    if (path.includes('/assessments/edit')) {
      return "Edit Assessment";
    }
    if (path.includes('/assessments/preview')) {
      return "Preview Assessment";
    }
    if (path.includes('/assessments')) {
      return "Assessments";
    }
    if (path.includes('/reports')) {
      return "Reports";
    }
    if (path.includes('/report')) {
      return "Report";
    }
    if (path.includes('/settings')) {
      return "Settings";
    }
    if (path.includes('/billings')) {
      return "Billings";
    }
    if (path.includes('/pricing')) {
      return "Credits & Billing";
    }
    if (path.includes('/dashboard')) {
      return "Dashboard";
    }

    return "Dashboard";
  };

  return (
    <div
      className={styles.container}
      style={{ ["--credit-banner-h" as string]: `${bannerHeight}px` } as React.CSSProperties}
    >
      <BuyAdditionalCreditModal
        isOpen={showTopupModal}
        onClose={() => setShowTopupModal(false)}
        currentBalance={credits?.totalAvailable ?? 0}
      />

      <SideBar active={active} />
      <main className={`${styles.main} ${noPadding ? styles.noPadding : ""}`}>
        <PageHeader title={getPageTitle()} />
        {creditBannerVariant && (
          <CreditBanner
            ref={bannerRef}
            variant={creditBannerVariant}
            onTopup={() => setShowTopupModal(true)}
          />
        )}
        {/* Push page content below the fixed header, plus the pinned credit
            banner when shown. Done here (once) rather than per page so it's
            immune to any margin/padding utilities on individual pages' scroll
            containers; each page only needs to shrink its own scroll height by
            the banner (via the --credit-banner-h variable). */}
        <div className='!mt-[76px]' style={{ paddingTop: bannerHeight }}>{children}</div>
      </main>
    </div>
  );
};

export { EmployerDashboardLayout };
